export const ESTADOS_TAREA_COLORS = {
  PENDIENTE: {
    bg: "#FFF3E0",
    text: "#E65100",
    dot: "#FF9800",
    label: "Pendiente",
  },
  "EN PROCESO": {
    bg: "#E3F2FD",
    text: "#1565C0",
    dot: "#2196F3",
    label: "En Proceso",
  },
  TERMINADA: {
    bg: "#E8F5E9",
    text: "#2E7D32",
    dot: "#4CAF50",
    label: "Terminada",
  },
};

/**
 * Estados de facturas
 */
export const ESTADOS_FACTURA_COLORS = {
  PAGADO: {
    bg: "#E8F5E9",
    text: "#2E7D32",
    dot: "#4CAF50",
    label: "Pagado",
  },
  PENDIENTE: {
    bg: "#FFF3E0",
    text: "#E65100",
    dot: "#FF9800",
    label: "Pendiente",
  },
  FACTURADO: {
    bg: "#E3F2FD",
    text: "#1565C0",
    dot: "#2196F3",
    label: "Facturado",
  },
  ABONO: {
    bg: "#F3E5F5",
    text: "#6A1B9A",
    dot: "#9C27B0",
    label: "Abono",
  },
  ANULADO: {
    bg: "#FFEBEE",
    text: "#C62828",
    dot: "#F44336",
    label: "Anulado",
  },
  CORRECCIÓN: {
    bg: "#FFEBEE",
    text: "#C62828",
    dot: "#F44336",
    label: "Anulada",
  },
};

/**
 * Estados de planes
 */
export const ESTADOS_PLAN_COLORS = {
  ACTIVO: {
    bg: "#E8F5E9",
    text: "#2E7D32",
    dot: "#4CAF50",
    label: "Activo",
  },
  INACTIVO: {
    bg: "#FFEBEE",
    text: "#C62828",
    dot: "#F44336",
    label: "Inactivo",
  },
};

/**
 * Estados de acceso al portal de clientes
 */
export const ESTADOS_PORTAL_COLORS = {
  ACTIVO: {
    bg: "#E8F5E9",
    text: "#2E7D32",
    dot: "#4CAF50",
    label: "Portal Activo",
  },
  INACTIVO: {
    bg: "#F5F5F5",
    text: "#757575",
    dot: "#BDBDBD",
    label: "Sin acceso",
  },
};

/**
 * Obtener colores de estado de acceso al portal
 * @param {boolean|number} portalActivo - 1/true = activo, 0/false = inactivo
 */
export const getPortalEstadoColors = (portalActivo) => {
  const key = portalActivo ? "ACTIVO" : "INACTIVO";
  return ESTADOS_PORTAL_COLORS[key];
};

/**
 * Obtener colores de estado de plan
 * @param {string} estado - ACTIVO | INACTIVO
 */
export const getPlanEstadoColors = (estado) => {
  return (
    ESTADOS_PLAN_COLORS[estado?.toUpperCase()] || {
      bg: "#F5F5F5",
      text: "#757575",
      dot: "#BDBDBD",
      label: estado || "Desconocido",
    }
  );
};

/**
 * Tipos de tarea
 */
export const TIPOS_TAREA_COLORS = {
  MES: {
    bg: "#E3F2FD",
    text: "#1565C0",
    label: "Mensual",
  },
  AÑO: {
    bg: "#F3E5F5",
    text: "#6A1B9A",
    label: "Anual",
  },
  ESPECIAL: {
    bg: "#FFF3E0",
    text: "#E65100",
    label: "Especial",
  },
};

/**
 * Colores base del UI
 */
export const UI_COLORS = {
  background: {
    default: "#F5F5F5",
    paper: "#FFFFFF",
    filters: "#FAFAFA",
    card: "#FFFFFF",
  },
  borders: {
    light: "#E0E0E0",
    medium: "#BDBDBD",
    dark: "#9E9E9E",
  },
  text: {
    primary: "#424242",
    secondary: "#757575",
    disabled: "#9E9E9E",
  },
};

// ==================== FUNCIONES HELPER ====================

/**
 * Obtiene los colores para un estado de tarea
 * @param {string} estado - Estado de la tarea
 * @returns {object} Objeto con bg, text, dot
 */
export const getTareaEstadoColors = (estado) => {
  return (
    ESTADOS_TAREA_COLORS[estado] || {
      bg: "#F5F5F5",
      text: "#757575",
      dot: "#BDBDBD",
      label: estado,
    }
  );
};

/**
 * Obtiene los colores para un estado de factura
 * @param {string} estado - Estado de la factura
 * @returns {object} Objeto con bg, text, dot
 */
export const getFacturaEstadoColors = (estado) => {
  return (
    ESTADOS_FACTURA_COLORS[estado] || {
      bg: "#F5F5F5",
      text: "#757575",
      dot: "#BDBDBD",
      label: estado,
    }
  );
};

/**
 * Obtiene los colores para un tipo de tarea
 * @param {string} tipo - Tipo de tarea (MES, AÑO, ESPECIAL)
 * @returns {object} Objeto con bg, text
 */
export const getTipoTareaColors = (tipo) => {
  return (
    TIPOS_TAREA_COLORS[tipo] || {
      bg: "#F5F5F5",
      text: "#757575",
      label: tipo,
    }
  );
};

/**
 * Calcula si una factura está atrasada
 * @param {string} fechaFacturacion - Fecha de facturación
 * @param {string} estadoFactura - Estado de la factura
 * @returns {number} Días de atraso (0 si no hay atraso)
 */
export const calcularDiasAtraso = (fechaFacturacion, estadoFactura) => {
  // Solo calcular atraso si está pendiente o con abono
  if (!["PENDIENTE", "FACTURADO", "ABONO"].includes(estadoFactura)) {
    return 0;
  }

  if (!fechaFacturacion) return 0;

  const hoy = new Date();
  const fechaFact = new Date(fechaFacturacion);
  const diffTime = hoy - fechaFact;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  // Considerar atraso después de 30 días
  return diffDays > 30 ? diffDays - 30 : 0;
};

/**
 * Obtiene el estilo de badge según días de atraso
 * @param {number} diasAtraso - Días de atraso
 * @returns {object} Objeto con backgroundColor y color
 */
export const getAtrasoStyle = (diasAtraso) => {
  if (diasAtraso === 0) return null;

  if (diasAtraso > 60) {
    return {
      backgroundColor: "#D32F2F",
      color: "#FFFFFF",
    };
  } else if (diasAtraso > 30) {
    return {
      backgroundColor: "#F57C00",
      color: "#FFFFFF",
    };
  } else {
    return {
      backgroundColor: "#FFA726",
      color: "#FFFFFF",
    };
  }
};

// ==================== ESTILOS BASE ====================

/**
 * Estilos base para cards
 */
export const cardStyles = {
  borderRadius: 12,
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
  },
};

/**
 * Estilos base para panels colapsables
 */
export const panelStyles = {
  header: {
    backgroundColor: UI_COLORS.background.filters,
    borderRadius: 12,
    border: `1px solid ${UI_COLORS.borders.light}`,
    padding: "12px 16px",
    cursor: "pointer",
    transition: "background-color 0.15s ease",
    "&:hover": {
      backgroundColor: UI_COLORS.background.default,
    },
  },
  content: {
    padding: "20px",
    border: `1px solid ${UI_COLORS.borders.light}`,
    borderTop: "none",
    borderRadius: "0 0 12px 12px",
    backgroundColor: UI_COLORS.background.paper,
  },
};

/**
 * Estilos base para badges
 */
export const badgeStyles = {
  fontSize: "0.75rem",
  fontWeight: 500,
  padding: "4px 12px",
  borderRadius: 6,
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
};

/**
 * Estilos base para chips de filtros
 */
export const filterChipStyles = {
  height: 28,
  fontSize: "0.75rem",
  fontWeight: 500,
  transition: "all 0.15s ease",
  "&:hover": {
    transform: "scale(1.03)",
  },
};

/**
 * Estilos base para inputs de búsqueda
 */
export const searchInputStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
    backgroundColor: UI_COLORS.background.filters,
    transition: "background-color 0.15s ease",
    "&:hover": {
      backgroundColor: UI_COLORS.background.default,
    },
    "&.Mui-focused": {
      backgroundColor: UI_COLORS.background.paper,
    },
  },
};
